import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { isEmpty, startCase } from 'lodash';
import { FeeCategory, FinanceType } from '@upstart/harmony/lib/constants';
import addCircleImage from '../../../assets/add-circle.png';
import closeImage from '../../../assets/close.png';
import { DealerLaunchStepProps, FeeRow, FeeTable } from '../types';
import {
  TextField,
  TextFieldValidationState,
} from '../../Input/TextField/TextField';
import {
  DealerFee,
  DealerFees,
  isFixedValueDealerFee,
} from '../../../types/dealerTypes';
import { Button } from '../../Input/Button/Button';
import { Tabs, TabsRef } from '../../Tabs/Tabs';
import { getDefaultFees } from '../defaults';
import { Checkbox } from '../../Input/Checkbox/Checkbox';
import { currencyValidatorFunction } from '../../../utils/validation';
import styles from '../styles.module.scss';
import { convertFeeTableToDealerFees } from '../../../utils/conversions';

// This page was rewritten from react-hook-form due to some issues regarding dynamic rows and item deletion
// See https://getprodigy.atlassian.net/browse/REL-3217 for details

/**
 * Converts dealers.fees to a format more easily used by this page
 */
const convertDealerFeesToFeeTable = (fees: DealerFees): FeeTable => {
  const result: Partial<FeeTable> = {};
  Object.values(FinanceType).forEach((financeType) => {
    const rows: FeeRow[] = [];
    // fees are only taken from the New condition when reading, as this page currently does not support
    // separating fees by condition
    (Object.entries(fees.New?.[financeType] || {}) as [
      FeeCategory,
      DealerFee[] | undefined,
    ][]).forEach(([category, feeList]) => {
      const taxable =
        category === FeeCategory.TAXABLE ||
        category === FeeCategory.CAPITALIZED_COST_TAXABLE;
      const capCost =
        category === FeeCategory.CAPITALIZED_COST ||
        category === FeeCategory.CAPITALIZED_COST_TAXABLE;

      feeList?.forEach((fee) => {
        // drop apr fees
        if (!isFixedValueDealerFee(fee)) return;

        rows.push({
          name: fee.name,
          value: fee.value,
          taxable,
          capCost,
          includeInFeeStack: fee.includeInFeeStack,
        });
      });
    });

    result[financeType] = rows;
  });

  return result as FeeTable;
};

export const DealerLaunchPricingFees: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const defaultFeeTable =
    !dealer.fees || isEmpty(dealer.fees)
      ? getDefaultFees()
      : convertDealerFeesToFeeTable(dealer.fees);
  const [leaseFees, setLeaseFees] = useState(defaultFeeTable.lease || []);
  const [cashFees, setCashFees] = useState(defaultFeeTable.cash || []);
  const [purchaseFees, setPurchaseFees] = useState(
    defaultFeeTable.purchase || [],
  );
  const tabsRef = useRef<TabsRef>(null);

  const deleteFee = ({
    index,
    feesList,
    setFeesList,
  }: {
    index: number;
    feesList: Array<FeeRow>;
    setFeesList: Dispatch<SetStateAction<FeeRow[]>>;
  }) => {
    const modifiedFees = [...feesList];
    modifiedFees.splice(index, 1);
    setFeesList(modifiedFees);
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    let hasErrors = false;
    const validationFunction = (financeType: FinanceType) => ({
      name,
      value,
    }: FeeRow) => {
      if (hasErrors) {
        return true;
      }
      if (!name || currencyValidatorFunction(value?.toString())) {
        tabsRef.current?.setActiveChildByKey(financeType);
        hasErrors = true;
        return true;
      }
      return false;
    };

    cashFees.some(validationFunction(FinanceType.CASH));
    purchaseFees.some(validationFunction(FinanceType.PURCHASE));
    if (!dealer.isIndependent) {
      leaseFees.some(validationFunction(FinanceType.LEASE));
    }
    if (hasErrors) {
      return true;
    }
    nextStep(
      {
        fees: convertFeeTableToDealerFees({
          cash: cashFees,
          lease: leaseFees,
          purchase: purchaseFees,
        }),
        id: dealer.id,
      },
      { disableDealerUpdateMerge: true },
    );
    return null;
  };

  const renderFeeInput = (
    financeType: FinanceType,
    fee: FeeRow,
    index: number,
    feesList: Array<FeeRow>,
    setFeesList: Dispatch<SetStateAction<FeeRow[]>>,
  ) => {
    const { name, value, taxable, capCost } = fee;
    const nameValidation: TextFieldValidationState = name
      ? { valid: true }
      : { valid: false, message: 'This field is required' };
    const currencyMessage = currencyValidatorFunction(value);
    const currencyValidation: TextFieldValidationState = currencyMessage
      ? { valid: false, message: currencyMessage }
      : { valid: true };
    return (
      <div
        className={classNames(styles.stepRow, styles.feeRow)}
        key={`${index}length${feesList.length}`}
      >
        <TextField
          label={index === 0 ? 'Name' : ''}
          name={`${financeType}[${index}].name` as never}
          width="md"
          value={name}
          validationState={nameValidation}
          onChange={(e) => {
            const newFees = [...feesList];
            newFees[index].name = e.target.value;
            setFeesList(newFees);
          }}
        />
        <TextField
          label={index === 0 ? 'Value' : ''}
          name={`${financeType}[${index}].value` as never}
          width="sm"
          type="number"
          value={value === undefined ? '' : value}
          validationState={currencyValidation}
          onChange={(e) => {
            const newFees = [...feesList];
            newFees[index].value = Number(e.target.value);
            setFeesList(newFees);
          }}
        />
        <div className={styles.feeCheckboxContainer}>
          <span
            className={classNames(
              'field-label',
              index !== 0 && styles.invisibleFeeLabel,
            )}
          >
            Taxable
          </span>
          <Checkbox
            className={styles.feeCheckbox}
            label=""
            name={`${financeType}[${index}].taxable` as never}
            variant="vertical"
            value={taxable}
            onChange={() => {
              const newFees = [...feesList];
              newFees[index].taxable = !taxable;
              setFeesList(newFees);
            }}
          />
        </div>
        {financeType === FinanceType.LEASE && (
          <div className={styles.feeCheckboxContainer}>
            <span
              className={classNames(
                'field-label',
                index !== 0 && styles.invisibleFeeLabel,
              )}
            >
              Cap Cost
            </span>
            <Checkbox
              className={styles.feeCheckbox}
              label=""
              name={`${financeType}[${index}].capCost` as never}
              variant="vertical"
              value={capCost}
              onChange={() => {
                const newFees = [...feesList];
                newFees[index].capCost = !capCost;
                setFeesList(newFees);
              }}
            />
          </div>
        )}
        <Button
          className={styles.deleteFeeButton}
          title="Delete Fee"
          variant="minimal"
          onClick={() => deleteFee({ index, feesList, setFeesList })}
        >
          <img src={closeImage} alt="Delete" />
        </Button>
      </div>
    );
  };

  const renderStep = (
    financeType: FinanceType,
    feesList: Array<FeeRow>,
    setFeesList: Dispatch<SetStateAction<FeeRow[]>>,
  ) => (
    <div key={financeType} className={styles.stepCard}>
      <h1>
        Let&apos;s Add Your Fees For{' '}
        {financeType === FinanceType.PURCHASE
          ? 'Finance'
          : startCase(financeType)}{' '}
        Purchase
      </h1>
      {feesList.map((value, index) =>
        renderFeeInput(financeType, value, index, feesList, setFeesList),
      )}
      <Button
        variant="minimal"
        onClick={() =>
          setFeesList([
            ...feesList,
            {
              name: '',
              taxable: false,
              capCost: false,
              includeInFeeStack: true,
            },
          ])
        }
      >
        <img className={styles.addIcon} src={addCircleImage} alt="" /> Add New
        Fee
      </Button>
      <div className={styles.pageNav}>
        <Button
          className={styles.pageNavButton}
          label="Previous"
          onClick={prevStep}
        />
        <div className="spacer" />
        <Button
          className={styles.pageNavButton}
          label="Continue"
          type="submit"
          variant="primary"
        />
      </div>
    </div>
  );

  const getPageTabs = () => {
    const tabs = [
      {
        key: FinanceType.CASH,
        name: 'Cash',
        render: () => renderStep(FinanceType.CASH, cashFees, setCashFees),
      },
      {
        key: FinanceType.PURCHASE,
        name: 'Finance',
        render: () =>
          renderStep(FinanceType.PURCHASE, purchaseFees, setPurchaseFees),
      },
      {
        key: FinanceType.LEASE,
        name: 'Lease',
        render: () => renderStep(FinanceType.LEASE, leaseFees, setLeaseFees),
      },
    ];
    return dealer.isIndependent ? tabs.filter((t) => t.name !== 'Lease') : tabs;
  };

  return (
    <form
      className={classNames(styles.stepContainer, styles.feesStep)}
      onSubmit={onSubmit}
    >
      <div className={styles.stepTitle}>Let&apos;s Add Your Fees</div>
      <Tabs ref={tabsRef} className={classNames(styles.tabs)}>
        {getPageTabs()}
      </Tabs>
    </form>
  );
};

export const test = {
  convertDealerFeesToFeeTable,
  convertFeeTableToDealerFees,
};
